import React, { useState } from 'react';
import '../styles/SmallChat.css';
import { ChatActionRequired, ChatIcon, ExitScreen, IconButton, Input, RedfinAnalysis, Tooltip, Typewriter } from './';
import { Chevron, Exit, Sparkle, Wand } from '../assets/icons';
import { colour, formatterCompact } from '../styles/GlobalStyles';
import { GuestHouseIcon } from 'hugeicons-react';
import { renderHTML } from '../functions';

function SmallChat(props) {
    const arvMessages = props.arvMessages;
    const loading = props.loading;
    const textAreaHeight = props.textAreaHeight;
    const compsChanged = props.compsChanged;
    const getARV = props.getARV;
    const arvLoading = props.arvLoading;
    const typeOutText = props.typeOutText;
    const setTypeOutText = props.setTypeOutText;
    const readOnly = props.readOnly;
    const arvDescription = props.arvDescription;
    const arvDescriptionPrompt = props.arvDescriptionPrompt;
    const onChangePrompt = props.onChangePrompt;
    const submitPrompt = props.submitPrompt;
    const setTextAreaHeight = props.setTextAreaHeight;
    const arvEstimate = props.arvEstimate;
    const arvRange = props.arvRange;
    const scenarios = props.scenarios;
    const actionRequired = props.actionRequired;
    const selectScenario = props.selectScenario;
    const expanded = props.expanded;
    const setExpanded = props.setExpanded;
    const smallChatHover = props.smallChatHover;
    const setSmallChatHover = props.setSmallChatHover;
    const smallChatHoverText = props.smallChatHoverText;
    const innerWidth = props.innerWidth;
    const anomaly = props.anomaly;
    const redfinData = props.redfinData;
    const [clickExpanded, setClickExpanded] = useState(null);

    const openPanel = () => {
        setExpanded(true);
        setSmallChatHover(false);
    };

    const closePanel = () => {
        setExpanded(false);
    };

    const textFinished = () => {
        setTypeOutText(false);
    };

    const values = [
        {
            label: "ARV",
            value: arvEstimate === null ? "Reasoning.." : formatterCompact.format(arvEstimate)
        },
        {
            label: "Range",
            value: arvRange === null ? "Reasoning.." : `${formatterCompact.format(arvRange.lower)} - ${formatterCompact.format(arvRange.upper)}`
        }
    ];

    return (
        <div className="small-chat-outer-container">
            {
                clickExpanded !== null &&
                <ExitScreen
                    clickScreen={() => setClickExpanded(null)}
                />
            }
            {
                expanded === false && innerWidth < 1280 ?
                <ChatIcon
                    hoverEffect={true}
                    toggleClick={() => openPanel()}
                    expanded={smallChatHover}
                    setExpanded={setSmallChatHover}
                    hoverText={smallChatHoverText}
                />
                :
                <div 
                    className="small-chat-inner-container relative-container"
                    style={{ '--text-area-height': `${textAreaHeight}px` }}
                >
                    <div className="small-chat-header-top-row">
                        <div className="small-chat-header-arv-estimate">
                            {
                                values.map((item, index) =>
                                    <div 
                                        className="small-chat-header-arv-estimate-element"
                                        key={index}
                                    >
                                        <span className="body-regular colour-primary">
                                            {item.label}
                                        </span>
                                        <span className="body-bold colour-primary">
                                            {item.value}
                                        </span>
                                    </div>
                                )
                            }
                        </div>
                    </div>
                    {
                        innerWidth < 1280 &&
                        <div className="small-chat-header-icons-row">
                            <IconButton
                                size="medium"
                                icon={<Exit fill={colour.gray07} size={16} className="modal-exit-icon" clickFunc={null} />}
                                disabled={false}
                                clickFunc={() => closePanel()}
                                standardIcon={false}
                                background={true}
                                bgType={"white"}
                                contextTooltip={false}
                                contextTooltipBG={null}
                                contextTooltipText={null}
                                customClass={null}
                            />
                        </div>
                    }
                    <div className="small-chat-description-container">
                        <div className="small-chat-text-container">
                            {
                                readOnly === true ?
                                <div 
                                    className="small-chat-text-element"
                                >
                                    <div className="small-chat-sparkle-container">
                                        <Sparkle
                                            fill={colour.gray06}
                                            className=""
                                            gradient={false}
                                        />
                                    </div>
                                    <div className="small-chat-text-content-container small-chat-text-assistant-width">
                                        {
                                            typeOutText === true ?
                                            <Typewriter
                                                text={arvDescription}
                                                delay={10}
                                                commaDelay={0}
                                                fullStopDelay={0} 
                                                play={true}
                                                className="body-regular colour-primary block-text white-space-pre-wrap typewriter-text-gap"
                                                isHTML={true}
                                                onComplete={textFinished}
                                            />
                                            :
                                            <div
                                                className={"body-regular colour-primary typewriter-text-gap"}
                                                dangerouslySetInnerHTML={{ __html: renderHTML(arvDescription) }}
                                            />
                                        }
                                    </div>
                                </div>
                                :
                                arvMessages.map((item, index) =>
                                    item.role === "system" || item.role === "user" ?
                                    null
                                    :
                                    item.role === "redfin_analysis" && redfinData === null ?
                                    null
                                    :
                                    item.role === "assistant" && item.expanded !== undefined && item.expanded !== null && item.arvSelection === true ?
                                    <div 
                                        className="small-chat-text-element-row small-chat-border-bottom"
                                        key={index}
                                    >
                                        <div className="small-chat-sparkle-container">
                                            <Sparkle
                                                fill={colour.gray06}
                                                className=""
                                                gradient={false}
                                            />
                                        </div>
                                        <div className="small-chat-content-outer-container">
                                            <div 
                                                className="small-chat-expand-container cursor-pointer relative-container"
                                                onClick={() => setClickExpanded(index)}
                                            >
                                                <span className="body-bold colour-primary">
                                                    {item.content}
                                                </span>
                                                <IconButton
                                                    size="x-small"
                                                    icon={<Chevron fill={colour.gray07} className="" />}
                                                    disabled={false}
                                                    clickFunc={() => clickExpanded === index ? setClickExpanded(null) : setClickExpanded(index)}
                                                    standardIcon={false}
                                                    background={false}
                                                    bgType={"white"}
                                                    contextTooltip={false}
                                                    contextTooltipBG={null}
                                                    contextTooltipText={null}
                                                    customClass={null}
                                                />
                                                {
                                                    clickExpanded === index &&
                                                    <Tooltip
                                                        type="info"
                                                        title={"ARV Comparables"}
                                                        body={item.expanded}
                                                        markdown={true}
                                                        position={"top"}
                                                        contextTooltipBg={null}
                                                        exit={false}
                                                        exitFunc={null}
                                                        customClass={"small-chat-tooltip-container"}
                                                    />
                                                }
                                            </div>
                                            {
                                                anomaly !== null &&
                                                <div className="small-chat-anomalies-container">
                                                    <div className="small-chat-anomalies-section-container">
                                                        <span className="body-bold colour-primary">
                                                            Potential dilemmas
                                                        </span>
                                                        <span className="body-regular colour-primary">
                                                            {anomaly.summary}
                                                        </span>
                                                    </div>
                                                    <div className="small-chat-anomalies-section-container">
                                                        <span className="body-bold colour-primary">
                                                            Recommendation
                                                        </span>
                                                        <ul>
                                                            {
                                                                anomaly.explanations.map((explanation, ii) =>
                                                                    <li key={ii} className="body-regular colour-primary">
                                                                        {explanation}
                                                                    </li>
                                                                )
                                                        }
                                                        </ul>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                    :
                                    item.role === "assistant" && item.expanded !== undefined && item.expanded !== null && item.arvRange !== undefined && item.arvRange !== null ?
                                    <div 
                                        className="small-chat-text-element-row"
                                        key={index}
                                    >
                                        <div className="small-chat-sparkle-container">
                                            <Sparkle
                                                fill={colour.gray06}
                                                className=""
                                                gradient={false}
                                            />
                                        </div>
                                        <div className="small-chat-content-outer-container">
                                            <div 
                                                className="small-chat-expand-container cursor-pointer relative-container"
                                                onClick={() => setClickExpanded(index)}
                                            >
                                                <span className="body-bold colour-primary">
                                                    Expand full analysis
                                                </span>
                                                <IconButton
                                                    size="x-small"
                                                    icon={<Chevron fill={colour.gray07} className="" />}
                                                    disabled={false}
                                                    clickFunc={() => clickExpanded === index ? setClickExpanded(null) : setClickExpanded(index)}
                                                    standardIcon={false}
                                                    background={false}
                                                    bgType={"white"}
                                                    contextTooltip={false}
                                                    contextTooltipBG={null}
                                                    contextTooltipText={null}
                                                    customClass={null}
                                                />
                                                {
                                                    clickExpanded === index &&
                                                    <Tooltip
                                                        type="info"
                                                        title={"ARV Estimate"}
                                                        body={item.expanded}
                                                        markdown={true}
                                                        position={"top"}
                                                        contextTooltipBg={null}
                                                        exit={false}
                                                        exitFunc={null}
                                                        customClass={"small-chat-tooltip-container"}
                                                    />
                                                }
                                            </div>
                                            <div className="small-chat-arv-range-container">
                                                <span className="body-bold colour-primary">
                                                    ARV Estimate
                                                </span>
                                                <span className="body-regular colour-primary">
                                                    {formatterCompact.format(item.arvRange.lower)} - {formatterCompact.format(item.arvRange.upper)}
                                                </span>
                                            </div>
                                            <div className="">
                                                <span className="body-regular colour-primary">
                                                    {item.content}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    :
                                    item.role === "assistant" && item.expanded !== undefined && item.expanded !== null ?
                                    <div className="small-chat-text-element">
                                        <div className="small-chat-sparkle-container">
                                            <Sparkle
                                                fill={colour.gray06}
                                                className=""
                                                gradient={false}
                                            />
                                        </div>
                                        <div className="small-chat-expand-container">
                                            <span className="body-regular colour-primary">
                                                {item.content}
                                            </span>
                                        </div>
                                    </div>
                                    :
                                    item.role === "redfin_analysis" && redfinData !== null ?
                                    <RedfinAnalysis
                                        redfinData={redfinData}
                                        darkMode={false}
                                        key={index}
                                    />
                                    :
                                    item.role === "action_required" ?
                                    <ChatActionRequired
                                        scenarios={scenarios}
                                        actionRequired={actionRequired}
                                        textFinished={textFinished}
                                        typeOutText={typeOutText}
                                        selectScenario={selectScenario}
                                        timeline={false}
                                        darkMode={false}
                                        key={index}
                                    />
                                    :
                                    item.role === "event" ?
                                    <div 
                                        className="small-chat-event-element"
                                        key={index}
                                    >
                                        <div className="small-chat-event-border">
                                        </div>
                                        {
                                            item.content.includes("Comps selection") ?
                                            <GuestHouseIcon size={13} color={colour.gray05} />
                                            :
                                            item.content.includes("ARV Estimate Regenerated") || item.content.includes("strategy selected") ?
                                            <Wand fill={colour.gray05} className="small-chat-description-wand-icon" />
                                            :
                                            null
                                        }
                                        <span className="body-regular colour-secondary block-text text-align-center">
                                            {item.content}
                                        </span>
                                        <div className="small-chat-event-border">
                                        </div>
                                    </div>
                                    :
                                    <div 
                                        className={"small-chat-text-element " + (item.role === "user_chat" ? "small-chat-text-bubble" : "")}
                                        key={index}
                                    >
                                        {
                                            item.role === "assistant" &&
                                            <div className="small-chat-sparkle-container">
                                                <Sparkle
                                                    fill={colour.gray06}
                                                    className=""
                                                    gradient={false}
                                                />
                                            </div>
                                        }
                                        <div className={"small-chat-text-content-container " + (item.role === "assistant" ? "small-chat-text-assistant-width" : "")}>
                                            {
                                                (index + 1) === arvMessages.length && item.role === "assistant" && typeOutText === true ?
                                                <Typewriter
                                                    text={item.content}
                                                    delay={10}
                                                    commaDelay={0}
                                                    fullStopDelay={0} 
                                                    play={true}
                                                    className="body-regular colour-primary block-text white-space-pre-wrap typewriter-text-gap"
                                                    isHTML={true}
                                                    onComplete={textFinished}
                                                />
                                                :
                                                <div
                                                    className={"body-regular colour-primary typewriter-text-gap"}
                                                    dangerouslySetInnerHTML={{ __html: renderHTML(item.content) }}
                                                />
                                            }
                                        </div>
                                    </div>
                                )
                            }
                            {
                                arvLoading === true ?
                                <div className="small-chat-event-element">
                                    <span className="body-bold colour-primary">
                                        Regenerating ARV...
                                    </span>
                                </div>
                                :
                                compsChanged === true && loading === false ?
                                <div className="small-chat-event-element">
                                    <div 
                                        className="text-button button-row gap-x-small"
                                        onClick={() => getARV()}
                                    >
                                        <Wand fill={colour.gray05} className="small-chat-description-wand-icon" />
                                        <span className="body-regular colour-primary">
                                            Regenerate ARV
                                        </span>
                                    </div>
                                </div>
                                :
                                null
                            }
                            {
                                loading === true &&
                                <div className="small-chat-text-element">
                                    <span className="body-bold colour-primary">
                                        Reasoning..
                                    </span>
                                </div>
                            }
                        </div>
                    </div>
                    <div className="small-chat-input-container">
                        <Input
                            label={null}
                            labelType={"default"}
                            secondaryLabelGrey={null}
                            info={false}
                            value={arvDescriptionPrompt}
                            onChange={onChangePrompt}
                            placeholder={"Ask anything about the analysis"}
                            type="text"
                            leftUnit={null}
                            rightUnit={null}
                            unitSwitch={false}
                            leftUnitSelected={true}
                            unitSwitchFunc={null}
                            disabled={arvLoading === true || loading === true ? true : false}
                            multiline={true}
                            error={false}
                            onMouseEnter={null}
                            onMouseLeave={null}
                            reset={false}
                            resetFunc={null}
                            resetPosition={null}
                            focusMultiline={false}
                            prompt={true}
                            submitPromptDisabled={arvDescriptionPrompt.trim() === "" ? true : false}
                            submitPrompt={() => arvDescriptionPrompt.trim() === "" ? null : submitPrompt()}
                            onHeightChange={setTextAreaHeight}
                        />
                    </div>
                </div>
            }
        </div>
    )
};

export default SmallChat;